import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import React, { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../components/Menu';
import type {
  TemplateMenuItemQuery,
  TemplateMenuItemQueryVariables,
  TemplatesMenuItemAllTopicsQuery,
} from '../graphql/generated/graphqlTypes';
import templatesIcon from '../icons/templates.svg';
import toTopicTreeList, {
  flattenTopicTreeList,
} from '../topic/toTopicTreeList';
import { isFeatureEnabled } from '../utils/features';
import templatePath, { savePreviousRoute } from '../utils/url/templatesPath';
import styles from './TemplatesMenuItem.module.scss';
import TemplateMenuItemRow from './TemplatesMenuItemRow';

const TEMPLATE_MENU_ITEM_QUERY = gql`
  query TemplateMenuItem($userId: ID!) {
    user(id: $userId) {
      id
      membershipTopics {
        id
        allPosts(orderBy: UPDATED_AT, type: TEMPLATE) {
          edges {
            node {
              id
              ...TemplateMenuItemRow_templatePost
            }
          }
        }
        templates(includeDescendants: true) {
          id
          topicOwner {
            id
            ancestors {
              id
            }
          }
          ...TemplateMenuItemRow_template
        }
      }
    }
  }
  ${TemplateMenuItemRow.fragments.template}
  ${TemplateMenuItemRow.fragments.templatePost}
`;

const ALL_TOPICS_QUERY = gql`
  query TemplatesMenuItemAllTopics {
    session: currentSession {
      organization {
        id
        topics {
          id
          templates {
            id
          }
          allPosts(orderBy: UPDATED_AT, type: TEMPLATE) {
            edges {
              node {
                id
              }
            }
          }
          ...toTopicTreeList_topic
        }
      }
    }
  }
  ${toTopicTreeList.fragments.topic}
`;

function useAllTopicsWithTemplates({ skip }: { skip: boolean }) {
  const allTopicsResponse = useQuery<TemplatesMenuItemAllTopicsQuery>(
    ALL_TOPICS_QUERY,
    {
      skip,
    },
  );

  return useMemo(() => {
    return flattenTopicTreeList(
      toTopicTreeList(
        allTopicsResponse.data?.session.organization.topics ?? [],
      ),
    );
  }, [allTopicsResponse]);
}

export const TemplatesMenuItemSubmenu = () => {
  const user = useAppSelector((state) => state.session.user);
  const recentlyUsedTemplateIds = useAppSelector(
    (state) => state.ui.recentlyUsedTemplateIds,
  ).slice(0, 5);

  const { data, loading } = useQuery<
    TemplateMenuItemQuery,
    TemplateMenuItemQueryVariables
  >(TEMPLATE_MENU_ITEM_QUERY, { variables: { userId: user.id } });

  const myTopicsTemplates = isFeatureEnabled('templates_v2')
    ? _.uniqBy(
        data?.user.membershipTopics.flatMap((topic) =>
          _.compact(topic.allPosts?.edges?.map((edge) => edge?.node)),
        ) ?? [],
        'id',
      ).slice(0, 5)
    : _.uniqBy(
        data?.user.membershipTopics.flatMap((topic) => topic.templates ?? []) ??
          [],
        'id',
      )
        .sort(
          (a, b) =>
            (a.topicOwner?.ancestors.length ?? 0) -
            (b.topicOwner?.ancestors.length ?? 0),
        )
        .slice(0, 5);

  const allTopics = useAllTopicsWithTemplates({
    skip: loading || myTopicsTemplates.length > 0,
  });

  const allTopicsTemplateIds = isFeatureEnabled('templates_v2')
    ? allTopics
        .filter((topic) => _.compact(topic.allPosts?.edges).length > 0)
        .sort((a, b) => (a.ancestors.length ?? 0) - (b.ancestors.length ?? 0))
        .flatMap((topic) =>
          _.compact(topic.allPosts?.edges?.map((edge) => edge?.node?.id)),
        )
        .slice(0, 5)
    : allTopics
        .filter((topic) => topic.templates.length > 0)
        .sort((a, b) => (a.ancestors.length ?? 0) - (b.ancestors.length ?? 0))
        .flatMap((topic) => topic.templates.map((template) => template.id))
        .slice(0, 5);

  const hasAnyTemplate =
    recentlyUsedTemplateIds.length > 0 ||
    myTopicsTemplates.length > 0 ||
    allTopicsTemplateIds.length > 0;

  return (
    <div className={styles.subMenu}>
      {recentlyUsedTemplateIds.length > 0 && (
        <>
          <div className={styles.header}>Recently used</div>
          {recentlyUsedTemplateIds.map((templateId) => (
            <TemplateMenuItemRow key={templateId} templateId={templateId} />
          ))}
          <MenuSeparator />
        </>
      )}
      {myTopicsTemplates.length > 0 && (
        <>
          <div className={styles.header}>My topics</div>
          {myTopicsTemplates.map((template) => (
            <TemplateMenuItemRow key={template.id} template={template} />
          ))}
          <MenuSeparator />
        </>
      )}
      {allTopicsTemplateIds.length > 0 && (
        <>
          <div className={styles.header}>All topics</div>
          {allTopicsTemplateIds.map((templateId) => (
            <TemplateMenuItemRow key={templateId} templateId={templateId} />
          ))}
          <MenuSeparator />
        </>
      )}
      {!hasAnyTemplate && (
        <>
          <div className={styles.header}>Recently used</div>
          <div className={styles.empty}>
            You haven’t used any templates recently.
          </div>
          <MenuSeparator />
        </>
      )}
      <MenuItem to={templatePath()} onClick={() => savePreviousRoute()}>
        View all templates
      </MenuItem>
    </div>
  );
};

const TemplatesMenuItem = () => (
  <MenuItem icon={templatesIcon} subMenuItems={<TemplatesMenuItemSubmenu />}>
    Templates
  </MenuItem>
);

export default TemplatesMenuItem;
